import {Node, PropertyOrKeyValue} from "../../Node";
import {PNumber} from "../../properties/PNumber";
import {PBoolean} from "../../properties/PBoolean";
import {PThreeConstant} from "../../properties/PThreeConstant";
import Stats from "stats.js";
import * as _ from "lodash";

export class RendererNode extends Node {

    ShadowsEnabled: boolean;
    Shadows: boolean;
    ToneMapping: string;
    ToneMappingExposure: number;
    ShadowMapType: string;
    antialias: boolean;
    convertSRGB: boolean;
    gammaFactor: number;
    outputEncoding: number;
    FPS: boolean;
    stats: Stats;

    get renderer() {
        return APP.renderer;
    }

    createProperties() {
        return [
            new PThreeConstant({
                "name": "ToneMapping",
                "default": "NoToneMapping",
                "Comment": I18N("scene editor:rendernode tonemapping comment"),
                "selectOptions": [
                    "NoToneMapping",
                    "LinearToneMapping",
                    "ReinhardToneMapping",
                    "CineonToneMapping",
                    "ACESFilmicToneMapping",
                ],
                node: this
            }),
            new PNumber({
                "name": "ToneMappingExposure",
                "default": 1,
                "min": 0,
                "max": 10,
                "Comment": I18N("scene editor:rendernode tonemapping exposure comment"),
                "showInEditor": (p) => p.node.ToneMapping != "NoToneMapping",
                node: this
            }),
            new PBoolean({
                "name": "Shadows",
                "comment": I18N("scene editor:rendernode shadows comment"),
                "default": true,
                node: this
            }),
            new PThreeConstant({
                "name": "ShadowMapType",
                "default": "PCFSoftShadowMap",
                "Comment": I18N("scene editor:rendernode shadowmap type comment"),
                "selectOptions": [
                    "BasicShadowMap",
                    "PCFShadowMap",
                    "PCFSoftShadowMap",
                    "VSMShadowMap",
                ],
                node: this
            }),
            new PThreeConstant({
                "name": "outputEncoding",
                "default": "sRGBEncoding",
                "selectOptions": [
                    "LinearEncoding",
                    "sRGBEncoding",
                    "GammaEncoding"
                ],
                "comment": I18N("scene editor:rendernode output encoding comment"),
                node: this
            }),
            new PBoolean({
                "name": "convertSRGB",
                "comment": I18N("scene editor:rendernode convert SRGB comment"),
                "default": true,
                node: this
            }),
            new PNumber({
                "name": "gammaFactor",
                "default": 2,
                "min": 0,
                "max": 5,
                "comment": I18N("scene editor:rendernode gamma factor comment"),
                "showInEditor": (p) => p.node.outputEncoding == "GammaEncoding",
                node: this
            }),
            new PBoolean({
                "name": "antialias",
                "comment": I18N("scene editor:rendernode antialias comment"),
                "default": false,
                node: this
            }),
            new PBoolean({
                "name": "FPS",
                "default": false,
                "comment": I18N("scene editor:rendernode fps comment"),
                node: this
            }),
        ] as PropertyOrKeyValue[];
    }

    init() {
        super.init();
        this.updateScene();
        return this;
    }

    updateScene() {
        this._dataChanged = {};
        if (this.changed('Shadows')) {
            this.renderer.shadowMap.enabled = this.Shadows;
            this.renderer.shadowMap.needsUpdate = true;
        }
        if (this.changed('ShadowMapType')) {
            if (this.renderer.shadowMap.type !== THREE[this.ShadowMapType]) {
                this.renderer.shadowMap.type = THREE[this.ShadowMapType];
                this.renderer.shadowMap.needsUpdate = true;
                const dummy = new THREE.Group();
                Array.from(APP.scene.children).forEach(c => dummy.add(c));
                _.delay(() => Array.from(dummy.children).forEach(c => APP.scene.add(c)), 100);
            }
        }
        if ((this.changed('ToneMapping') || this.changed('ToneMappingExposure'))) {
            this.renderer.toneMapping = THREE[this.ToneMapping];
            this.renderer.toneMappingExposure = this.ToneMappingExposure;
        }
        if (this.changed('antialias')) {
            this.renderer.antialias = this.antialias;
        }
        if (this.changed('gammaFactor')) {
            this.renderer.gammaFactor = this.gammaFactor;
        }
        if (this.changed('outputEncoding')) {
            this.renderer.outputEncoding = THREE[this.outputEncoding];
        }
        if (this.changed('FPS')) {
            if (this.stats) {
                document.body.removeChild(this.stats.dom);
                STAGE.off('beforeRenderScene', this.stats.beginCallback);
                STAGE.off('afterRenderScene', this.stats.endCallback);
                this.stats = null;
            }
            if (this.FPS) {
                this.stats = new Stats();
                this.stats.beginCallback = () => this.stats.begin();
                this.stats.endCallback = () => this.stats.end();
                document.body.appendChild(this.stats.dom);
                STAGE.on('beforeRenderScene', this.stats.beginCallback);
                STAGE.on('afterRenderScene', this.stats.endCallback);
            }
        }
        if (this.changed('convertSRGB')) {
            STAGE.isSRGBConversion = this.convertSRGB;
        }
    }

    getName() {
        return "Renderer";
    }

}
